import { useCallback, useState } from 'react';
import { z } from 'zod';
import { rage } from '~/rage';

import { Button } from '../../components/Button';
import {
	ButtonContainer,
	FormContainer,
	PanelContainer,
} from '../../components/Containers';
import { FormTitle } from '../../components/FormTitle';
import { TextInput } from '../../components/TextInput';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import {
	AuthTabs,
	setActiveTab,
	setConfirmTimer,
	setEmailConfirm,
} from '../../redux/slices/authTabsSlice';
import {
	IFormError,
	setEmail,
	setLoading,
	setLogin,
	setNickname,
	setPassword,
	setPasswordRep,
} from '../../redux/slices/formDataSlice';
import {
	StyledPlaceholder,
	StyledPlaceholderContainer,
} from './Register.styled';

const accountEmailSchema = z
	.string()
	.min(1, 'Почта обязательна для заполнения')
	.email('Некорректный формат почты');

const accountLoginSchema = z
	.string()
	.min(1, 'Логин обязателен для заполнения')
	.min(5, 'Логин должен содержать минимум 5 символов')
	.max(32, 'Логин не должен превышать 32 символа');
// .regex(
// 	/^[a-zA-Z0-9_-]+$/,
// 	'Разрешены латиница, цифры, дефис и подчёркивание',
// );

const accountPasswordSchema = z
	.string()
	.min(1, 'Пароль обязателен для заполнения')
	.min(6, 'Пароль должен содержать минимум 6 символов')
	.max(64, 'Пароль не должен превышать 64 символа');
// .regex(/[a-zA-Z]/, 'Пароль должен содержать буквы')
// .regex(/\d/, 'Пароль должен содержать цифры');
// .regex(/[!@#$%^&*_-]/, 'Пароль должен содержать хотя бы один спецсимвол');

const registerSchema = z
	.object({
		login: accountLoginSchema,
		email: accountEmailSchema,
		password: accountPasswordSchema,
		passwordRep: z.string().min(1, 'Повторите пароль'),
	})
	.refine((data) => data.password === data.passwordRep, {
		message: 'Пароли не совпадают',
		path: ['passwordRep'],
	});

const Register = () => {
	const [promo, setPromo] = useState('');
	const [errors, setErrors] = useState<IFormError[]>([]);

	const email = useLayoutSelector((state) => state.formData.email);
	const login = useLayoutSelector((state) => state.formData.login);
	const password = useLayoutSelector((state) => state.formData.password);
	const passwordRep = useLayoutSelector((state) => state.formData.passwordRep);
	const loading = useLayoutSelector((state) => state.formData.loading);
	const dispatch = useLayoutDispatch();

	const handleSubmit = async () => {
		dispatch(setLoading(true));

		const errors: IFormError[] = [];
		try {
			await registerSchema.parseAsync({
				email,
				login,
				password,
				passwordRep,
			});

			const result = await rage.accounts.sendRegistrationCode(
				email,
				login,
				password,
			);

			dispatch(setEmail(result.maskedEmail));
			dispatch(setEmailConfirm('register'));
			dispatch(setConfirmTimer(Math.floor(result.resendAvailableAt / 1000)));
			dispatch(setActiveTab(AuthTabs.ConfirmEmail));
		} catch (error) {
			if (error instanceof z.ZodError) {
				error.issues.forEach((err) => {
					if (err.path && typeof err.path[0] === 'string') {
						errors.push({ source: err.path[0], text: err.message });
					}
				});
			} else {
				rage.notifications.showException(error);
			}
		}

		setErrors(errors);
		dispatch(setLoading(false));
	};

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;
			if (name === 'email') {
				dispatch(setEmail(value));
			} else if (name === 'nickname') {
				dispatch(setNickname(value));
			} else if (name === 'login') {
				dispatch(setLogin(value));
			} else if (name === 'password') {
				dispatch(setPassword(value));
			} else if (name === 'passwordRep') {
				dispatch(setPasswordRep(value));
			} else if (name === 'promo') {
				setPromo(value);
			}
			setErrors((prevErrors) =>
				prevErrors.filter((error) => error.source !== name),
			);
		},
		[],
	);

	const handleLoginClick = () => {
		dispatch(setActiveTab(AuthTabs.Login));
	};

	return (
		<PanelContainer>
			<FormTitle text={'Регистрация аккаунта'} />
			<FormContainer>
				<TextInput
					name="login"
					placeholder="Придумайте логин"
					error={errors.find((error) => error.source === 'login')?.text}
					defaultValue={login}
					onChange={handleInputChange}
					tabIndex={1}
				/>
				<TextInput
					name="email"
					placeholder="Введите адрес электронной почты"
					error={errors.find((error) => error.source === 'email')?.text}
					defaultValue={email}
					onChange={handleInputChange}
					tabIndex={2}
				/>
				<TextInput
					name="password"
					placeholder="Введите пароль"
					error={errors.find((error) => error.source === 'password')?.text}
					value={password}
					onChange={handleInputChange}
					tabIndex={3}
				/>
				<TextInput
					name="passwordRep"
					placeholder="Повторите пароль"
					error={errors.find((error) => error.source === 'passwordRep')?.text}
					value={passwordRep}
					onChange={handleInputChange}
					tabIndex={4}
				/>
				<StyledPlaceholderContainer>
					<TextInput
						name="promo"
						defaultValue={promo}
						onChange={handleInputChange}
						tabIndex={5}
					/>
					<StyledPlaceholder $show={!promo}>
						Промокод <span>(Если есть)</span>
					</StyledPlaceholder>
				</StyledPlaceholderContainer>
			</FormContainer>
			<ButtonContainer>
				<Button
					primary
					name="register"
					type="button"
					loading={loading}
					onClick={handleSubmit}
					tabIndex={6}
				>
					Продолжить
				</Button>
				<Button
					name="return"
					disabled={loading}
					onClick={handleLoginClick}
					tabIndex={7}
				>
					Вернуться назад
				</Button>
			</ButtonContainer>
		</PanelContainer>
	);
};

export { Register };
